import { c as _c } from "react/compiler-runtime";
import { type ReactElement } from 'react';
import Banner, { type BannerProps, type BannerButton } from '@precis-digital/kurama/Banner';
import ThemedPromoBanner, { type ThemedPromoBannerProps } from 'shared/components/Themed/PromoBanner';
import { sendGTMEvent } from 'shared/googleTagManager';
import { isAfter } from 'shared/dateFns';

/**
 * Base properties for tracked banner components
 */
interface BaseTrackedBannerProps {
  /** Unique identifier for the banner, used for tracking context */
  bannerId: string;
  /** Location where the banner is displayed, used for tracking context */
  location?: string;
  /** Optional button for learn more action with tracking */
  learnMoreButton?: BannerButton;
  /** Optional button for dismiss action with tracking */
  dismissButton?: BannerButton;
  /** Optional expiration date - banner won't render if current date is after this */
  expiryDate?: Date;
}
interface TrackedStandardBannerProps extends Omit<BannerProps, 'learnMoreButton' | 'dismissButton' | 'onClose'>, BaseTrackedBannerProps {
  type: 'banner';
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
interface TrackedPromoBannerProps extends Omit<ThemedPromoBannerProps, 'learnMoreButton' | 'dismissButton'>, BaseTrackedBannerProps {
  type: 'promoBanner';
}
type TrackedBannerProps = TrackedStandardBannerProps | TrackedPromoBannerProps;
const omitStandardBannerTrackingProps = (props: TrackedStandardBannerProps): Omit<TrackedStandardBannerProps, 'type' | 'bannerId' | 'location' | 'learnMoreButton' | 'dismissButton' | 'onClose' | 'expiryDate'> => {
  const {
    type,
    bannerId,
    location,
    learnMoreButton,
    dismissButton,
    onClose,
    expiryDate,
    ...rest
  } = props;
  return rest;
};
const omitPromoBannerTrackingProps = (props: TrackedPromoBannerProps): Omit<TrackedPromoBannerProps, 'type' | 'bannerId' | 'location' | 'learnMoreButton' | 'dismissButton' | 'expiryDate'> => {
  const {
    type,
    bannerId,
    location,
    learnMoreButton,
    dismissButton,
    expiryDate,
    ...rest
  } = props;
  return rest;
};
const withTracking = (button: BannerButton | undefined, action: 'learn_more' | 'dismiss' | 'snooze', meta: {
  bannerId: string;
  location?: string;
}): BannerButton | undefined => {
  if (button == null) {
    return undefined;
  }
  return {
    ...button,
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      sendGTMEvent({
        event: 'promo_banner_interaction',
        banner_id: meta.bannerId,
        action,
        location: meta.location
      });
      if ('onClick' in button) {
        button.onClick(e);
      }
    }
  };
};
const createTrackedOnClose = (onClose: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined, meta: {
  bannerId: string;
  location?: string;
}): ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined => {
  if (onClose == null) {
    return undefined;
  }
  return (e: React.MouseEvent<HTMLButtonElement>) => {
    sendGTMEvent({
      event: 'promo_banner_interaction',
      banner_id: meta.bannerId,
      action: 'snooze',
      location: meta.location
    });
    onClose(e);
  };
};

/**
 * A tracked banner component that wraps Banner or ThemedPromoBanner with GTM event tracking.
 * It wraps learnMore, dismiss and snooze buttons with sendGTM function.
 * @param props - The banner props (either standard or promo banner)
 * @returns The tracked banner component or null if expired
 */
export const TrackedBanner = props => {
  const $ = _c(33);
  const {
    type,
    bannerId,
    location,
    learnMoreButton,
    dismissButton,
    expiryDate
  } = props;
  if (expiryDate != null && isAfter(new Date(), expiryDate)) {
    return null;
  }
  if (type === "banner") {
    let t0;
    if ($[0] !== props) {
      t0 = omitStandardBannerTrackingProps(props);
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== bannerId || $[3] !== learnMoreButton || $[4] !== location) {
      t1 = withTracking(learnMoreButton, "learn_more", {
        bannerId,
        location
      });
      $[2] = bannerId;
      $[3] = learnMoreButton;
      $[4] = location;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== bannerId || $[7] !== dismissButton || $[8] !== location) {
      t2 = withTracking(dismissButton, "dismiss", {
        bannerId,
        location
      });
      $[6] = bannerId;
      $[7] = dismissButton;
      $[8] = location;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== bannerId || $[11] !== location || $[12] !== props.onClose) {
      t3 = createTrackedOnClose(props.onClose, {
        bannerId,
        location
      });
      $[10] = bannerId;
      $[11] = location;
      $[12] = props.onClose;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t0 || $[15] !== t1 || $[16] !== t2 || $[17] !== t3) {
      t4 = <Banner {...t0} learnMoreButton={t1} dismissButton={t2} onClose={t3} />;
      $[14] = t0;
      $[15] = t1;
      $[16] = t2;
      $[17] = t3;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  } else {
    let t0;
    if ($[19] !== props) {
      t0 = omitPromoBannerTrackingProps(props);
      $[19] = props;
      $[20] = t0;
    } else {
      t0 = $[20];
    }
    let t1;
    if ($[21] !== bannerId || $[22] !== learnMoreButton || $[23] !== location) {
      t1 = withTracking(learnMoreButton, "learn_more", {
        bannerId,
        location
      });
      $[21] = bannerId;
      $[22] = learnMoreButton;
      $[23] = location;
      $[24] = t1;
    } else {
      t1 = $[24];
    }
    let t2;
    if ($[25] !== bannerId || $[26] !== dismissButton || $[27] !== location) {
      t2 = withTracking(dismissButton, "dismiss", {
        bannerId,
        location
      });
      $[25] = bannerId;
      $[26] = dismissButton;
      $[27] = location;
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    let t3;
    if ($[29] !== t0 || $[30] !== t1 || $[31] !== t2) {
      t3 = <ThemedPromoBanner {...t0} learnMoreButton={t1} dismissButton={t2} />;
      $[29] = t0;
      $[30] = t1;
      $[31] = t2;
      $[32] = t3;
    } else {
      t3 = $[32];
    }
    return t3;
  }
};